'use client'

import textHeadingM from '@fukku/styles/texts/textHeadingM.module.css'
import { LinkType } from '@fukku/text-link/utils'
import { SelectPromotion } from 'analytics/components/SelectPromotion/SelectPromotion'
import cx from 'classix'
import dynamic from 'next/dynamic'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'
import type {
	CtaWithImage,
	PointOfInterestImage,
} from '../../../../../../Home.type'
import animationStyles from '../../../../MotionAnimations.module.css'
import focusStyle from '../../../../Shared/A11y/TabFocus.module.css'
import { BannerFullHeightWrapper } from '../../../../Shared/BannerFullHeightWrapper/BannerFullHeightWrapper'
import { HeroBannerImage } from '../../../components/HeroBannerImage/HeroBannerImage'
import styles from './HeroBannerShopMultibrand.module.css'

const HeroBannerShopMultibrandTitle = dynamic(() =>
	import('../HeroBannerShopMultibrandTitle/HeroBannerShopMultibrandTitle').then(
		(mod) => mod.HeroBannerShopMultibrandTitle
	)
)

const ClientTextLink = dynamic(() =>
	import('@fukku/text-link/client/ClientTextLink').then(
		(mod) => mod.ClientTextLink
	)
)

interface HeroBannerShopMultibrandContentProps {
	readonly titleKey: string
	readonly landscapeImage: PointOfInterestImage | null
	readonly portraitImage: PointOfInterestImage | null
	readonly ctasWithImage: CtaWithImage[]
	readonly inverse: boolean
	readonly isPrioritySlot: boolean | undefined
}

export function HeroBannerShopMultibrandContent({
	titleKey,
	landscapeImage,
	portraitImage,
	ctasWithImage,
	inverse,
	isPrioritySlot,
}: HeroBannerShopMultibrandContentProps) {
	const ctaTextClassName = cx(
		animationStyles.opacityFadeIn,
		styles.ctaText,
		textHeadingM.className,
		applyStylesIf(inverse, styles.inverse)
	)
	const [hoveredCtaId, setHoveredCtaId] = useState('')
	const { isLargeOrGreater } = useResponsive()
	const [isFirstRender, setIsFirstRender] = useState(true)
	const [isContentHovered, setIsContentHovered] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)

	useLayoutEffect(() => {
		setIsFirstRender(false)
	}, [])

	useEffect(() => {
		const handleContentHover = () => {
			setIsContentHovered(true)
		}

		if (contentRef.current) {
			contentRef.current.addEventListener('mouseenter', handleContentHover)
		}

		return () => {
			contentRef.current?.removeEventListener('mouseenter', handleContentHover)
		}
	}, [isFirstRender])

	const handleBrandImageChange = (newHoveredCtaId: string) => {
		setHoveredCtaId(newHoveredCtaId)
	}

	const handleDefaultImageChange = () => {
		setHoveredCtaId('')
	}

	return (
		<BannerFullHeightWrapper isPrioritySlot={isPrioritySlot}>
			{landscapeImage && portraitImage && (
				<HeroBannerImage
					priority={isPrioritySlot}
					landscapeImage={landscapeImage}
					portraitImage={portraitImage}
					className={cx(
						hoveredCtaId ? styles.hiddenImage : styles.visibleImage
					)}
				/>
			)}
			{isContentHovered || hoveredCtaId
				? ctasWithImage.map(({ ctaId, image }) => (
						<HeroBannerImage
							key={ctaId}
							image={image}
							className={cx(
								ctaId === hoveredCtaId
									? styles.visibleImage
									: styles.hiddenImage
							)}
						/>
					))
				: null}
			{!isFirstRender && (
				<div
					ref={contentRef}
					className={styles.heroBannerMultibrandTextCenterWrapper}
				>
					<HeroBannerShopMultibrandTitle
						titleKey={titleKey}
						inverse={inverse}
						isLargeOrGreater={isLargeOrGreater}
					/>
					{ctasWithImage.length === 1 ? (
						<div className={ctaTextClassName}>{ctasWithImage[0].text}</div>
					) : (
						<nav className={styles.ctasWrapper}>
							{ctasWithImage.map((cta) => (
								<button
									type='button'
									className={styles.ctaButton}
									key={cta.ctaId}
									onMouseEnter={() => handleBrandImageChange(cta.ctaId)}
									onFocus={() => handleBrandImageChange(cta.ctaId)}
									onMouseLeave={handleDefaultImageChange}
									onBlur={handleDefaultImageChange}
									tabIndex={-1}
								>
									<SelectPromotion promotion={cta.promotion}>
										<ClientTextLink
											href={cta.href}
											type={LinkType.Inspirational}
											inverse={inverse}
											linkProps={cta.linkProps}
											className={cx(
												animationStyles.opacityFadeIn,
												styles.ctaLink,
												focusStyle.bannerCtaFocus
											)}
											isLocalizedPath={cta.isLocalizedPath}
											withLegacyLocale={cta.withLegacyLocale}
										>
											{cta.text}
										</ClientTextLink>
									</SelectPromotion>
								</button>
							))}
						</nav>
					)}
				</div>
			)}
		</BannerFullHeightWrapper>
	)
}
